"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { useLocale } from "@/lib/locale-context";

// Боковая навигация разделов «О компании» и «Поддержка» — 1:1 со Солнышко
// (PageWithSidebar), акцент синий. Десктоп — фикс-сайдбар слева; мобайл —
// горизонтальные чипсы сверху. Контент (bare) идёт в правую колонку.
const ACCENT = "#0039A6";

const ABOUT_LINKS = [
  { href: "/about", key: "nav.about" },
  { href: "/about/events", key: "sidebar.events" },
  { href: "/about/faq", key: "nav.faq" },
  { href: "/about/partnership", key: "sidebar.partnership" },
  { href: "/about/loyalty", key: "menu.loyalty" },
  { href: "/about/bonus", key: "menu.bonus" },
];

const SUPPORT_LINKS = [
  { href: "/support/responsibility", key: "sidebar.responsibility" },
  { href: "/support/accident", key: "sidebar.accident" },
  { href: "/support/fines", key: "sidebar.fines" },
  { href: "/support/documents", key: "sidebar.documents" },
];

function useIsMobile(): boolean {
  const [m, setM] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
}

export default function PageWithSidebar({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { t, href } = useLocale();
  const isMobile = useIsMobile();
  const links = pathname.includes("/support") ? SUPPORT_LINKS : pathname.includes("/about") ? ABOUT_LINKS : null;
  if (!links) return <>{children}</>;

  if (isMobile) {
    return (
      <div style={{ padding: "20px 16px 40px" }}>
        <nav style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 8, marginBottom: 20, WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}>
          {links.map((link) => {
            const active = pathname === href(link.href);
            return (
              <Link key={link.href} href={href(link.href)} style={{
                flexShrink: 0, padding: "8px 14px", fontSize: 13,
                color: active ? "#fff" : "#333", fontWeight: active ? 600 : 400,
                textDecoration: "none", background: active ? ACCENT : "#F0F0F5",
                borderRadius: 999, whiteSpace: "nowrap",
              }}>{t(link.key)}</Link>
            );
          })}
        </nav>
        <div>{children}</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "50px 20px 85px", display: "flex", gap: 40 }}>
      <nav style={{ width: 240, flexShrink: 0 }}>
        <div style={{ position: "sticky", top: 90 }}>
          {links.map((link) => {
            const active = pathname === href(link.href);
            return (
              <Link key={link.href} href={href(link.href)} style={{
                display: "block", padding: "10px 16px", fontSize: 15,
                color: active ? ACCENT : "#333", fontWeight: active ? 600 : 400,
                textDecoration: "none",
                borderLeft: active ? `3px solid ${ACCENT}` : "3px solid transparent",
                background: active ? "#EAF0FB" : "transparent",
                borderRadius: "0 8px 8px 0", transition: "all 0.15s",
              }}>{t(link.key)}</Link>
            );
          })}
        </div>
      </nav>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}
